import api from './client';
import {get, post, del} from './http';
import {type FlagColor} from './flags';

export type Submission = {
  id: string;
  content_id: string;
  item_id: string | null;
  item_external_id: string | null;
  classification_id: string | null;
  handle: string | null;
  source_url: string | null;
  display_name: string | null;
  submission_type: 'Actor' | 'Content';
  affiliate: string;
  platform: string;
  policy_violation: string;
  // Every risk the analyst picked under policy_violation, in selection order. risks[0] is the
  // primary - what Apollo receives and what QC edits.
  risks: string[];
  action_status:
    | 'none'
    | 'in hopper'
    | 'pending qc review'
    | 'held for p0'
    | 'sent to client'
    | 'escalated'
    | 'delivery failed'
    | 'not sent - removed'
    | 'pending'
    // A soft-removed stream (DELETE /api/submissions/{id}) - GetByExternalId deliberately doesn't
    // filter these out (an analyst probing "did I already submit this?" needs to see a prior
    // removal too), so this value reaches the client even though the main list endpoint excludes it.
    | 'removed';
  created_by: string;
  created_at: string;
  // When this was actually sent to the client - distinct from created_at (submitted/classified).
  // Null if not yet delivered, or a historic row whose export didn't carry the date.
  delivered_at: string | null;
  notes: string | null;
  platform_status: 'available' | 'removed' | 'restricted' | 'unknown' | null;
  platform_feedback_message: string | null;
  // The server's classification of platform_feedback_message (PlatformFeedbackMessageCatalog), null
  // when no rule matched. Optional: only the list endpoint fills it.
  platform_reason_code?: string | null;
  // Last platform re-check attempt, whether or not it found anything new. Null before the first
  // check, and always null for a historic row.
  last_status_check_at: string | null;
  // Restrictions the platform applied after first observation, as stable keys - see PLATFORM_CHANGES
  // in SubmissionsTable. Null for a historic row.
  platform_changes?: string[] | null;
  language: string[];
  topic: string[];
  location: string[];
  core_risk: string[];
  // Bulk-imported from the old platform's export - never backed by a submission stream, so no
  // stream-scoped action (retry/send-to-apollo/remove/history) is valid against this id.
  is_historic: boolean;
  // Actor-only example videos, carried through so a Resubmit flow can prefill its enrichment form
  // from this submission. Null for a historic row (the export never carried it).
  example_ids: string | null;
  // Which resubmission this is - 1 for an ordinary submission, 2+ for an explicit analyst
  // amendment/resubmit in the same vertical. Always 1 for a historic row.
  resubmission_ordinal: number;
  // At-a-glance row badges - whether this submission has a completed calibration, has calibration
  // notes (completed or not), or has any reviewer comment. Always false for a historic row (neither has anywhere to attach).
  has_calibration: boolean;
  has_calibration_notes: boolean;
  has_reviewer_comments: boolean;
  // The classification carries analyst screenshots (internal QA evidence) - list endpoint only.
  has_attachments?: boolean;
  // The signed-in analyst's own flag on this row's item - flags live on items only, so set it with
  // itemsApi.setFlags([item_id]), and two rows of one item always show the same flag. Personal, so a
  // colleague's flag never shows here. Always null for a historic row or one with no item_id.
  flag: FlagColor | null;
  // The client's outcome from an uploaded feedback file (CustomerFeedbackUploadDialog). Null when no
  // upload has covered this submission.
  customer_feedback?: CustomerFeedback | null;
  // The item's scraped title (the video's title). Null for a historic row or one with no item.
  title?: string | null;
  // The item's scraped author (channel) name. display_name is the title whenever the item has one,
  // so the grid reads this instead. List endpoint only.
  author_name?: string | null;
  thumbnail_url?: string | null;
};

export type CustomerFeedback = {
  last_decision: string | null;
  live_status: string | null;
  flag_day: string;
  uploaded_at: string;
  uploaded_by: string;
  // Falcon only: the customer's note on their action, and how many pins they queued for action.
  notes: string | null;
  pins_actioned: number | null;
};

export type CustomerFeedbackChange = {
  submission_id: string;
  is_historic: boolean;
  external_id: string;
  policy_violation: string;
  display_name: string | null;
  is_new: boolean;
  old_last_decision: string | null;
  new_last_decision: string | null;
  old_live_status: string | null;
  new_live_status: string | null;
  flag_day: string;
  old_notes: string | null;
  new_notes: string | null;
  old_pins_actioned: number | null;
  new_pins_actioned: number | null;
};

export type CustomerFeedbackUpload = {
  file_sha256: string;
  rows: number;
  invalid: number;
  duplicates_collapsed: number;
  unmatched: number;
  new: number;
  changed: number;
  unchanged: number;
  unknown_verticals: Array<{vertical: string; rows: number}>;
  // New and changed rows, changes to existing feedback first, capped server-side.
  changes: CustomerFeedbackChange[];
  changes_truncated: boolean;
  applied: boolean;
  // Which customer layout the file was read as: "Pioneer" or "Falcon bad actors".
  format: string;
  // The file's column titles, and the one each value was read from (null: none found) - the
  // uploader can re-point a value, which is sent back as a column mapping.
  columns: string[];
  fields: CustomerFeedbackField[];
};

export type CustomerFeedbackField = {
  key: string;
  label: string;
  required: boolean;
  column: string | null;
};

// Field key to the file's column title; '' means "no column".
export type ColumnMapping = Record<string, string>;

export type SubmissionsQuery = {
  affiliate?: string;
  policy_violation?: string[];
  risk?: string[];
  // Taxonomy node ids (policy violation, sub-category or risk). A risk name alone is ambiguous across
  // policy violations; a node id is scoped to its own branch.
  risk_node?: string[];
  action_status?: string[];
  submission_type?: string[];
  platform?: string;
  date_from?: string;
  date_to?: string;
  analyst?: string[];
  language?: string[];
  topic?: string[];
  location?: string[];
  core_risk?: string[];
  has_calibration?: boolean;
  has_calibration_notes?: boolean;
  has_reviewer_comments?: boolean;
  thumbnail_changed?: boolean;
  platform_status?: string[];
  // Flag colours, plus 'none' for "rows I haven't flagged" - always the caller's own flags.
  flag?: string[];
  // 'any' (has customer feedback), a decision value from CUSTOMER_DECISIONS, or 'none'.
  customer_feedback?: string[];
  q?: string;
  page?: number;
  page_size?: number;
  view_all?: boolean;
  include_historic?: boolean;
  // Export only: cap to the first N rows of the list's ordering ("export current" = the rows paged in).
  max_rows?: number;
};

export type PolicyViolationStat = {
  policy_violation: string;
  total: number;
  in_hopper: number;
  sent: number;
  sent_today: number;
  sent_this_week: number;
  sent_this_month: number;
};

export type PolicyViolationCount = {
  policy_violation: string;
  count: number;
};

export type SubmissionStats = {
  by_policy_violation: PolicyViolationStat[];
  total: number;
  sent: number;
  in_hopper: number;
  sent_today: number;
  sent_this_week: number;
  sent_this_month: number;
  analysts: string[];
};

// One time bucket of deliveries for one policy violation and primary risk. `date` is the bucket's
// first local day - the day, the Monday of the week, or the 1st of the month - per the granularity
// requested. `risk` is empty for a submission with no risk prong; a by-violation view sums across it.
export type PolicyViolationDeliveryCount = {
  date: string;
  policy_violation: string;
  risk: string;
  count: number;
  // Submissions whose delivery failed, bucketed by when they were submitted.
  failed: number;
};

export type DeliveryGranularity = 'day' | 'week' | 'month';

export type SubmissionList = {
  results: Submission[];
  page: number;
  page_size: number;
  total_count: number;
};

export type SubmissionHistoryItem = {
  id: string;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  platform: string;
  published_at: string | null;
  collected_at: string;
  author_id: string | null;
  author_name: string | null;
};

export type SubmissionHistoryJob = {
  id: string;
  search_term: string | null;
  source: string;
  created_by: string;
  created_at: string;
};

export type SubmissionHistoryClassification = {
  id: string;
  analyst_email: string;
  policy_violation: string;
  risks: string[];
  notes: string | null;
  created_at: string;
  origin:
    'moderation_queue' | 'investigations' | 'single_submission' | 'image_search' | 'follower_sweep';
};

export type SubmissionHistoryP0AlertLink = {
  id: string;
  subject: string | null;
  status: string;
  created_at: string;
};

export type SubmissionHistoryEvent = {
  type: string;
  occurred_at: string;
  actor_email: string | null;
  detail: string | null;
  // Only on calibration events: the day it happened or is planned for (yyyy-MM-dd).
  calibration_date?: string | null;
};

export type SubmissionPlatformStatus = {
  status: 'available' | 'removed' | 'restricted' | 'unknown' | null;
  reason_code: string | null;
  raw_message: string | null;
  // Content (video) only.
  age_restricted: boolean | null;
  content_warning: boolean | null;
  made_for_kids: boolean | null;
  safe_for_minors: boolean | null;
  restricted_regions: string | null;
  // Actor (channel) only.
  is_family_safe: boolean | null;
  // Both - from different underlying platform fields depending on submission_type.
  is_channel_monetized: boolean | null;
  verified: boolean | null;
  last_checked_at: string | null;
  status_changed_at: string | null;
  // "scheduled" (the routine sweep) or "pre-submission" (the Pioneer API target's own liveness check,
  // found gone before ever attempting to report it) - lets the UI say a removed status was discovered
  // while trying to submit, not just that it happened at some point.
  last_check_kind: string | null;
};

export type SubmissionHistory = {
  item: SubmissionHistoryItem | null;
  job: SubmissionHistoryJob | null;
  classification: SubmissionHistoryClassification | null;
  p0_alerts: SubmissionHistoryP0AlertLink[];
  platform_status: SubmissionPlatformStatus;
  timeline: SubmissionHistoryEvent[];
  submission_type: 'Actor' | 'Content';
  // From the submission itself, not the (possibly-missing) item join - lets a caller build a
  // P0LinkedItem for this one submission (e.g. the Slack "create P0 email" deep link) without a
  // second lookup.
  item_external_id: string | null;
  policy_violation: string;
  // The client's outcome from an uploaded feedback file - the timeline's customer feedback milestone.
  customer_feedback?: CustomerFeedback | null;
};

export const submissionsApi = {
  // The date window here is the same one the list endpoint takes, so the tiles can describe exactly
  // the rows on screen. Omitted by callers that want the whole caseload (the dashboard).
  stats: async (
    viewAll = false,
    submissionType?: 'Actor' | 'Content',
    dateFrom?: string,
    dateTo?: string,
    allVerticals = false
  ) =>
    get<SubmissionStats>('/submissions/stats', {
      view_all: viewAll || undefined,
      submission_type: submissionType,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      all_verticals: allVerticals || undefined
    }),

  deliveryStats: async (
    granularity: DeliveryGranularity = 'day',
    buckets = 30,
    viewAll = false,
    submissionType?: 'Actor' | 'Content',
    allVerticals = false
  ) =>
    get<PolicyViolationDeliveryCount[]>('/submissions/daily-stats', {
      granularity,
      buckets,
      view_all: viewAll || undefined,
      submission_type: submissionType,
      all_verticals: allVerticals || undefined
    }),

  list: async (parameters?: SubmissionsQuery) => get<SubmissionList>('/submissions', parameters),

  // Per-vertical counts under the list's own filters, for the grid's vertical tabs. The server
  // ignores policy_violation here, so a selected tab doesn't zero out its siblings.
  policyViolationCounts: async (parameters?: SubmissionsQuery) =>
    get<PolicyViolationCount[]>('/submissions/policy-violation-counts', parameters),

  existingByExternalId: async (platform: string, externalId: string) =>
    get<Submission[]>('/submissions/by-external-id', {platform, external_id: externalId}),

  history: async (id: string) => get<SubmissionHistory>(`/submissions/${id}/history`),

  // Appends a SubmissionCalibrated event. State-preserving server-side, so a delivered submission
  // stays delivered - the response is the unchanged submission, refetched.
  recordCalibration: async (
    id: string,
    body: {comment: string; completed: boolean; calibration_date: string | null}
  ) => post<typeof body, Submission>(`/submissions/${id}/calibration`, body),

  retry: async (id: string) => post<undefined, Submission>(`/submissions/${id}/retry`),

  // Live or historic ids. Only marks them due: PRI.Scheduler sends the checks on its next tick, so the
  // result shows up minutes later, not in this response. `queued` leaves out rows it can't check.
  requestStatusCheck: async (ids: string[]) =>
    post<{ids: string[]}, {queued: number}>('/submissions/status-check', {ids}),

  // A second, independent outbound action: pushes the submission to Apollo without re-triggering the
  // client delivery. Publishing is asynchronous (PRI.DeliveryDispatcher's Apollo relay), so a 200 here
  // means "queued", not "sent" - the timeline's "Sent to Apollo" entry is what confirms the publish.
  sendToApollo: async (id: string) =>
    post<undefined, Submission>(`/submissions/${id}/send-to-apollo`),

  remove: async (id: string, reason: string) =>
    del<void, {reason: string}>(`/submissions/${id}`, {reason}),

  // Two steps: preview writes nothing; apply sends the same file back with the preview's hash, so
  // only what the uploader reviewed can be written.
  // The customer is the deployment's own - the server infers it, so the file is all that's sent.
  async previewCustomerFeedback(file: File, mapping?: ColumnMapping) {
    const formData = new FormData();
    formData.append('file', file);
    if (mapping) {
      formData.append('column_mapping', JSON.stringify(mapping));
    }

    return post<FormData, CustomerFeedbackUpload>(
      '/submissions/customer-feedback/preview',
      formData
    );
  },

  async applyCustomerFeedback(file: File, fileSha256: string, mapping?: ColumnMapping) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_sha256', fileSha256);
    if (mapping) {
      formData.append('column_mapping', JSON.stringify(mapping));
    }

    return post<FormData, CustomerFeedbackUpload>('/submissions/customer-feedback', formData);
  },

  async exportCsv(parameters?: SubmissionsQuery) {
    const {data} = await api.get<Blob>('/submissions/export', {
      params: parameters,
      responseType: 'blob'
    });
    return data;
  }
};
